import { Injectable } from "@angular/core";
import { BaseManager } from "./base.manager";
import { LocalStorageRepositoryInterface } from "../../datastore/local/localstorage.interface";
import { UserRepositoryInterface } from "../../datastore/repositories/user/user.interface";
import { Observable } from "rxjs";
import { concatMap, map, tap } from "rxjs/operators";
import { UserModel } from "../../datastore/entities/user.model";
import { ResourceModel } from "../../datastore/entities/resource.model";
import { LoginResponseModel } from "../../datastore/entities/loginresponse.model";
import { ChangePasswordResponseModel } from "../../datastore/entities/changepasswordresponse.model";
import { InfoPlantUserModel } from "../../datastore/entities/infoplantuser.model";

@Injectable()
export class UserManager extends BaseManager {
    constructor(
        protected localStorageRepository: LocalStorageRepositoryInterface,
        private userRepository: UserRepositoryInterface) {
        super(localStorageRepository);
    }

    public login(email: string, password: string): Observable<{}> {
        return this.userRepository.login(email, password).pipe(
            tap((loginModel: LoginResponseModel) => {
                this.localStorageRepository.setToken(loginModel.token);
                this.localStorageRepository.setRefreshToken(loginModel.refreshToken);
            }),
            concatMap(() => this.userRepository.getLoggedUser()
                .pipe(
                    tap((user: UserModel) => {
                        this.localStorageRepository.setLoggedEmail(user.email);
                        this.localStorageRepository.setLoggedUserId(user.uuid);
                    })
                )
            )
        );
    }

    public logout(): void {
        this.localStorageRepository.removeToken();
        this.localStorageRepository.removeRefreshToken();
        this.localStorageRepository.removeLoggedEmail();
        this.localStorageRepository.removeLoggedUserId();
        this.localStorageRepository.removeInfoPlantToken();
        this.localStorageRepository.removeCurrentLanguage();
    }

    public getProfile(id: number): Observable<UserModel> {
        return this.userRepository.getProfile(id);
    }

    public getLoggedUserEmail(): string {
        return this.localStorageRepository.getLoggedEmail();
    }

    public getInfoPlantUser(email: string, password: string): Observable<boolean> {
        return this.userRepository.getInfoPlantUser(email, password).pipe(
            tap((infoPlantUserModel: InfoPlantUserModel) => {
                if (infoPlantUserModel.userAuth) {
                    this.localStorageRepository.setInfoPlantToken(infoPlantUserModel.email);
                }
            }),
            map((infoPlantUserModel) => {
                return infoPlantUserModel.userAuth;
            })
        );
    }

    public getLoggedUserId(): string {
        return this.localStorageRepository.getLoggedUserId();
    }

    public createUser(email: string): Observable<UserModel> {
        return this.userRepository.createUser(email);
    }

    public changePassword(oldPassword: string, newPassword: string): Observable<ChangePasswordResponseModel> {
        return this.userRepository.changePassword(oldPassword, newPassword, "me");
    }

    public getResources(): Observable<ResourceModel[]> {
        return this.userRepository.getResources();
    }

    public updateLoggedUser(): Observable<UserModel> {
        return this.userRepository.getLoggedUser().pipe(
            tap((user: UserModel) => {
                this.localStorageRepository.setLoggedEmail(user.email);
                this.localStorageRepository.setLoggedUserId(user.uuid);
            })
        );
    }
}
